/**
 * Custom Sticky Cart – Mamaearth floating "View cart" pill.
 * Syncs with Shopify cart only (no GoKwik). Opens native cart drawer + fireViewCart.
 *
 * Performance: updates as soon as add.js / change.js / clear.js succeed
 * (optimistic merge), instead of waiting for the follow-up cart.js + drawer events.
 */
(function () {
  "use strict";

  const nativeFetch = window.fetch.bind(window);
  const fetchCart = () => nativeFetch("/cart.js").then((r) => r.json());

  const excludedPaths = [
    "/cart",
    "/checkout",
    "/pages/orders",
    "/pages/skin-assessment",
    "/pages/my-assessments",
    "/pages/refer",
    "/pages/wallet",
    "/pages/educate",
    "/apps/return_prime",
  ];

  const isCustomersPage = () =>
    Array.from(document.body.classList || []).some(
      (c) =>
        c.startsWith("hdt-page-type-customers") ||
        c.startsWith("template-customers"),
    );

  const isExcludedPage = () => {
    const path = typeof location !== "undefined" ? location.pathname : "";
    if (isCustomersPage()) return true;
    if (document.body.classList.contains("template-cart")) return true;
    // Meta PDP has its own sticky ATC + View cart; hide the floating pill
    if (document.body.classList.contains("meta-ad-visitor")) return true;
    if (window.metaAdData && window.metaAdData.isMetaAd) return true;
    return excludedPaths.some((p) => path === p || path.indexOf(p + "/") === 0);
  };

  const container = () => document.getElementById("custom-sticky-cart");
  const imagesEl = () =>
    document.querySelector("[data-custom-sticky-cart-images]");
  const countEl = () =>
    document.querySelector("[data-custom-sticky-cart-count]");
  const trigger = () =>
    document.querySelector("[data-custom-sticky-cart-trigger]");

  let lastItemCount = -1;
  let lastLineCount = 0;
  let lastKnownCart = null;
  let lastSignature = "";
  /** Line key that must stay visually in front (most recently added / bumped). */
  let lastFrontKey = null;
  /** Previous front key — used as the second (behind) thumbnail. */
  let prevFrontKey = null;

  function lineImage(item) {
    if (!item) return "";
    var src = "";
    if (typeof item.image === "string") src = item.image;
    else if (item.featured_image) {
      src = item.featured_image.url || item.featured_image.src || "";
    }
    if (!src) return "";
    return src.indexOf("//") === 0 ? "https:" + src : src;
  }

  function lineKey(item) {
    if (!item) return "";
    return item.key || String(item.variant_id || item.id || "");
  }

  function rememberFrontKey(key) {
    if (!key) return;
    if (key !== lastFrontKey) {
      if (lastFrontKey) prevFrontKey = lastFrontKey;
      lastFrontKey = key;
    }
  }

  /**
   * Newest thumbnail first (front), one older behind — independent of cart.items order.
   * Cart.js may list oldest-first; optimistic merge may differ; without pinning, the
   * latest image flashes in front then swaps behind on reconcile.
   */
  function getDisplayItems(cart) {
    var withImg = (cart.items || []).filter(function (i) {
      return !!lineImage(i);
    });
    if (!withImg.length) return [];
    if (withImg.length === 1) {
      rememberFrontKey(lineKey(withImg[0]));
      return withImg;
    }

    var newestIdx = -1;
    if (lastFrontKey) {
      newestIdx = withImg.findIndex(function (i) {
        return lineKey(i) === lastFrontKey;
      });
    }
    if (newestIdx < 0) {
      // Shopify Ajax cart: most recently added line is last
      newestIdx = withImg.length - 1;
    }

    var newest = withImg[newestIdx];
    var second = null;
    if (prevFrontKey && prevFrontKey !== lineKey(newest)) {
      second =
        withImg.find(function (i) {
          return lineKey(i) === prevFrontKey;
        }) || null;
    }
    if (!second) {
      for (var i = withImg.length - 1; i >= 0; i--) {
        if (i === newestIdx) continue;
        second = withImg[i];
        break;
      }
    }

    rememberFrontKey(lineKey(newest));
    return second ? [newest, second] : [newest];
  }

  function detectFrontKeyFromCartDelta(prev, next) {
    if (!next || !Array.isArray(next.items)) return null;
    if (!prev || !Array.isArray(prev.items) || !prev.items.length) {
      return next.items.length
        ? lineKey(next.items[next.items.length - 1])
        : null;
    }

    for (var i = 0; i < next.items.length; i++) {
      var n = next.items[i];
      var nKey = lineKey(n);
      var p = prev.items.find(function (x) {
        return lineKey(x) === nKey;
      });
      if (!p) return nKey;
      if ((parseInt(n.quantity, 10) || 0) > (parseInt(p.quantity, 10) || 0)) {
        return nKey;
      }
    }

    if (
      lastFrontKey &&
      next.items.some(function (i) {
        return lineKey(i) === lastFrontKey;
      })
    ) {
      return lastFrontKey;
    }
    return next.items.length
      ? lineKey(next.items[next.items.length - 1])
      : null;
  }

  /**
   * True only when a cart line was fully removed (qty → 0).
   * Qty-only decreases (3 → 2) keep the same items.length — never pop-out.
   * Do not compare line `key`s: optimistic add.js vs change.js/cart.js can use
   * different key shapes and falsely look like a removal.
   */
  function hasFullyRemovedLine(prev, next) {
    if (!prev || !Array.isArray(prev.items) || !prev.items.length) return false;
    if (!next || !Array.isArray(next.items)) return true;
    if (next.items.length >= prev.items.length) return false;

    // Extra guard: at least one variant id must be gone
    var nextVariants = {};
    next.items.forEach(function (item) {
      var id = String(item.variant_id || item.id || "");
      if (id) nextVariants[id] = true;
    });
    return prev.items.some(function (item) {
      var id = String(item.variant_id || item.id || "");
      return id && !nextVariants[id];
    });
  }

  function cartSignature(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      return "empty:" + (cart && cart.item_count ? cart.item_count : 0);
    }
    // Order-independent so cart.js reorder alone does not thrash the DOM;
    // front key is appended separately in applyCart.
    var parts = cart.items
      .map(function (i) {
        return lineKey(i) + ":" + (i.quantity || 0);
      })
      .sort();
    return (cart.item_count || 0) + "|" + parts.join(",");
  }

  function normalizeAddLines(data) {
    if (!data || typeof data !== "object") return [];
    if (Array.isArray(data.items) && data.items.length) return data.items;
    if (data.id != null || data.variant_id != null || data.key) return [data];
    return [];
  }

  /** Build a cart-shaped object from add.js line(s) so UI need not wait for cart.js */
  function mergeAddIntoCart(prev, data) {
    var lines = normalizeAddLines(data);
    if (!lines.length) return prev;

    var cart = {
      items:
        prev && Array.isArray(prev.items)
          ? prev.items.map(function (i) {
              return Object.assign({}, i);
            })
          : [],
    };

    lines.forEach(function (line) {
      // Match on key OR id/variant_id — never gate out the id/variant_id
      // fallback just because both sides happen to have a `key`. add.js and
      // cart.js can format `key` slightly differently (property ordering,
      // bundling apps, etc.); if that made an existing line look "new" here,
      // it got pushed as a duplicate and its already-counted quantity was
      // added a second time — the pill briefly doubling before the real
      // cart.js response (via the deferred cart:change event) corrected it.
      var idx = cart.items.findIndex(function (i) {
        if (line.key && i.key && i.key === line.key) return true;
        if (line.id != null && i.id != null && String(i.id) === String(line.id))
          return true;
        if (
          line.variant_id != null &&
          i.variant_id != null &&
          String(i.variant_id) === String(line.variant_id)
        )
          return true;
        return false;
      });
      var img = lineImage(line) || (idx >= 0 ? lineImage(cart.items[idx]) : "");
      var normalized = Object.assign({}, line, { image: img });

      if (idx >= 0) {
        cart.items[idx] = Object.assign({}, cart.items[idx], normalized);
      } else {
        cart.items.push(normalized);
      }
      rememberFrontKey(lineKey(normalized));
    });

    // Recompute from the merged lines rather than incremental subtract/add
    // math — this guarantees item_count always matches what `items` actually
    // holds, so a matching miss can no longer inflate the displayed count.
    cart.item_count = cart.items.reduce(function (sum, i) {
      return sum + (parseInt(i.quantity, 10) || 0);
    }, 0);

    return cart;
  }

  function requestUrl(input) {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url;
    try {
      return String(input);
    } catch (e) {
      return "";
    }
  }

  function interceptCartMutationResponse(url, response) {
    if (!response || !response.ok) return;
    var path = url || "";
    var isAdd = /(?:^|\/)cart\/add\.js(?:\?|$)/.test(path);
    var isChange = /(?:^|\/)cart\/change\.js(?:\?|$)/.test(path);
    var isClear = /(?:^|\/)cart\/clear\.js(?:\?|$)/.test(path);
    if (!isAdd && !isChange && !isClear) return;

    response
      .clone()
      .json()
      .then(function (data) {
        if (isAdd) {
          var merged = mergeAddIntoCart(lastKnownCart, data);
          if (merged) applyCart(merged, { detectFront: false });
          return;
        }
        if (isClear) {
          lastFrontKey = null;
          prevFrontKey = null;
        } else if (data && typeof data === "object") {
          var bumped = detectFrontKeyFromCartDelta(lastKnownCart, data);
          if (bumped) rememberFrontKey(bumped);
        }
        // change.js / clear.js already return a full cart object
        if (data && typeof data === "object") {
          applyCart(data, { detectFront: false });
        }
      })
      .catch(function () {});
  }

  // Observe ATC / qty / clear as soon as Shopify returns — before theme's cart.js
  if (!window.__mamaStickyCartFetchPatched) {
    window.__mamaStickyCartFetchPatched = true;
    window.fetch = function (input, init) {
      var isCartMutation = false;
      try {
        var url = requestUrl(input);
        var method = (
          (init && init.method) ||
          (input && input.method) ||
          "GET"
        ).toUpperCase();
        isCartMutation =
          method === "POST" &&
          /(?:^|\/)cart\/(add|change|clear)\.js(?:\?|$)/.test(url);
      } catch (e) {
        // Never let a bug in here break an unrelated request.
        return nativeFetch(input, init);
      }

      // Everything else on the page (analytics, chat, telemetry) passes through
      // untouched — this patch is global, so it must stay invisible to them.
      if (!isCartMutation) return nativeFetch(input, init);

      var promise = nativeFetch(input, init);

      /* Terminate the observer side-chain. `promise.then(fn)` on its own creates
         a derived promise that nobody handles, so a failed cart request would
         surface as "Uncaught (in promise) TypeError: Failed to fetch" even when
         the actual caller handled its own rejection. The returned `promise` is
         untouched, so callers still see the real result. */
      promise
        .then(function (res) {
          interceptCartMutationResponse(url, res);
        })
        .catch(function () {});

      return promise;
    };
  }

  let positionTicking = false;
  const POSITION_GAP = 10;

  /** Height the pill must clear for the PDP sticky ATC (fixed bottom:0). */
  function stickyAtcClearance() {
    const stickyAtc = document.querySelector(".hdt-sticky-btn-atc");
    if (!stickyAtc || stickyAtc.offsetHeight <= 0) return 0;
    const alwaysOn = stickyAtc.classList.contains(
      "hdt-always-sticky-btn-atc-on-mobile",
    );
    if (!document.body.classList.contains("sticky-shown") && !alwaysOn) {
      return 0;
    }
    const atcBottom = parseFloat(getComputedStyle(stickyAtc).bottom);
    return (
      (Number.isFinite(atcBottom) ? atcBottom : 0) + stickyAtc.offsetHeight
    );
  }

  /**
   * The "View all offers" tab sits on top of the strip but is absolutely
   * positioned, so it is not part of the strip's offsetHeight — measure it
   * separately or the pill lands on top of it.
   */
  function offersTabHeight(progress) {
    const tab = progress && progress.querySelector("[data-ctx-offers-tab]");
    if (!tab || tab.hidden) return 0;
    return tab.offsetHeight || 0;
  }

  /**
   * Stack pill above progress bar + toolbar using CSS layout bottoms.
   * Avoid window.innerHeight - getBoundingClientRect(): on mobile scroll the
   * address bar / visual viewport shifts that math and the pill overlaps the bar.
   */
  function updatePosition() {
    const wrap = container();
    if (!wrap) return;

    const mq = window.matchMedia("(max-width: 767px)");
    if (!mq.matches) return;

    const progress = document.querySelector(".cart-progressBar");
    const toolbar =
      document.querySelector(".hdt-section-toolbar-mobile .hdt-toolbar") ||
      document.querySelector(".hdt-toolbar-mobile .hdt-toolbar");

    let bottom = 80;
    const progressStyle = progress ? getComputedStyle(progress) : null;
    const progressVisible =
      progress &&
      progressStyle &&
      progressStyle.display !== "none" &&
      progressStyle.visibility !== "hidden" &&
      progress.offsetHeight > 0 &&
      progress.getAttribute("aria-hidden") !== "true";

    if (progressVisible) {
      var progBottom = parseFloat(progressStyle.bottom);
      if (Number.isNaN(progBottom)) {
        // Fallback: CSS var used by the contractual strip
        var cssVar = getComputedStyle(document.documentElement)
          .getPropertyValue("--hdt-cart-progress-bottom")
          .trim();
        progBottom = parseFloat(cssVar);
      }
      if (Number.isNaN(progBottom)) progBottom = 72;
      bottom = Math.round(
        progBottom +
          progress.offsetHeight +
          offersTabHeight(progress) +
          POSITION_GAP,
      );
    } else if (toolbar && toolbar.offsetHeight > 0) {
      const toolbarStyle = getComputedStyle(toolbar);
      var toolbarBottom = parseFloat(toolbarStyle.bottom);
      if (Number.isNaN(toolbarBottom)) toolbarBottom = 0;
      bottom = Math.round(
        toolbarBottom +
          toolbar.offsetHeight +
          (parseFloat(toolbarStyle.marginBottom) || 0) +
          POSITION_GAP,
      );
    }

    // Floor: never sit under the sticky ATC (Safari chrome collapse / missing strip)
    const atcClear = stickyAtcClearance();
    if (atcClear > 0) {
      bottom = Math.max(bottom, Math.round(atcClear + POSITION_GAP));
    }

    wrap.style.bottom = bottom + "px";
  }

  function schedulePositionUpdate() {
    if (positionTicking) return;
    positionTicking = true;
    requestAnimationFrame(function () {
      positionTicking = false;
      updatePosition();
    });
  }

  /**
   * Schedule a delayed position update to handle cases where the progress bar
   * is animating in or hasn't fully rendered yet. Used after initial cart add.
   */
  function scheduleDelayedPositionUpdate(delay) {
    delay = delay || 100;
    setTimeout(function () {
      schedulePositionUpdate();
    }, delay);
  }

  function rebuildImages(imgWrap, items, didAdd, isFirstItem) {
    imgWrap.innerHTML = "";
    items.forEach(function (item, index) {
      var src = lineImage(item);
      if (!src) return;
      var imgWrapEl = document.createElement("span");
      imgWrapEl.className =
        "custom-sticky-cart__img-wrap " +
        (index === 0
          ? "custom-sticky-cart__img-wrap--front"
          : "custom-sticky-cart__img-wrap--back");
      var img = document.createElement("img");
      img.src = src;
      img.alt = item.title || "";
      img.setAttribute("loading", "lazy");
      // Drop-in only when adding onto an already non-empty cart
      if (didAdd && index === 0 && !isFirstItem) {
        imgWrapEl.classList.add("custom-sticky-cart__img-wrap--drop-in");
        imgWrapEl.addEventListener(
          "animationend",
          function onDropEnd() {
            imgWrapEl.removeEventListener("animationend", onDropEnd);
            imgWrapEl.classList.remove("custom-sticky-cart__img-wrap--drop-in");
          },
          { once: true },
        );
      }
      imgWrapEl.appendChild(img);
      imgWrap.appendChild(imgWrapEl);
    });
  }

  function render(cart, opts) {
    const wrap = container();
    const imgWrap = imagesEl();
    const cnt = countEl();
    if (!wrap || !imgWrap || !cnt) return;
    opts = opts || {};

    lastKnownCart = cart || lastKnownCart;

    if (isExcludedPage()) {
      wrap.style.setProperty("display", "none", "important");
      wrap.setAttribute("aria-hidden", "true");
      wrap.classList.remove("custom-sticky-cart--animate-in");
      lastItemCount = 0;
      lastLineCount = 0;
      return;
    }

    if (!cart || !cart.items || !cart.items.length) {
      lastFrontKey = null;
      prevFrontKey = null;
      var isVisible =
        wrap.style.display !== "none" &&
        wrap.getAttribute("aria-hidden") !== "true";
      if (isVisible) {
        wrap.classList.remove("custom-sticky-cart--animate-in");
        wrap.classList.add("custom-sticky-cart--animate-out");
        wrap.addEventListener("animationend", function onEnd() {
          wrap.removeEventListener("animationend", onEnd);
          wrap.style.setProperty("display", "none", "important");
          wrap.setAttribute("aria-hidden", "true");
          wrap.classList.remove(
            "custom-sticky-cart--animate-out",
            "custom-sticky-cart--expanded",
            "custom-sticky-cart--compact",
            "custom-sticky-cart--reveal",
          );
        });
      } else {
        wrap.style.setProperty("display", "none", "important");
        wrap.setAttribute("aria-hidden", "true");
      }
      lastItemCount = 0;
      lastLineCount = 0;
      return;
    }

    // Newest (last-added) always first so it keeps z-index / drop-in on top
    const items = getDisplayItems(cart);
    const plural = cart.item_count === 1 ? "" : "s";
    const didAdd = lastItemCount >= 0 && cart.item_count > lastItemCount;
    // Pop-out only when a product line is fully removed (qty → 0), not 3 → 2.
    // Require fewer cart lines — qty-only updates keep the same items.length.
    const didRemove =
      lastItemCount > 0 &&
      cart.item_count < lastItemCount &&
      cart.item_count > 0 &&
      cart.items.length < lastLineCount &&
      !!opts.lineRemoved;
    const wasEmpty = lastItemCount === 0 || lastItemCount < 0;
    const isFirstItem = didAdd && wasEmpty;

    if (didRemove) {
      var firstImgWrap = imgWrap.querySelector(
        ".custom-sticky-cart__img-wrap:first-child",
      );
      if (firstImgWrap) {
        var rect = firstImgWrap.getBoundingClientRect();
        var imgCenterX = rect.left + rect.width / 2;
        var disappearY = rect.top - 80;

        firstImgWrap.classList.add("custom-sticky-cart__img-wrap--pop-out");

        setTimeout(function () {
          var gifOverlay = document.createElement("span");
          gifOverlay.className = "custom-sticky-cart__gif-overlay";
          gifOverlay.style.left = imgCenterX - 30 + "px";
          gifOverlay.style.top = disappearY - 30 + "px";
          var gifImg = document.createElement("img");
          gifImg.src =
            "https://cdn.shopify.com/s/files/1/0888/8057/8807/files/50x50.gif?v=1770967574";
          gifImg.alt = "";
          gifOverlay.appendChild(gifImg);
          document.body.appendChild(gifOverlay);

          setTimeout(function () {
            if (gifOverlay.parentNode)
              gifOverlay.parentNode.removeChild(gifOverlay);
          }, 700);
        }, 350);

        setTimeout(function () {
          rebuildImages(imgWrap, items, false, false);
          lastItemCount = cart.item_count;
          lastLineCount = cart.items.length;
          cnt.textContent = cart.item_count + " Item" + plural;
          updatePosition();
        }, 1000);
        return;
      }
    }

    rebuildImages(imgWrap, items, didAdd, isFirstItem);

    lastItemCount = cart.item_count;
    lastLineCount = cart.items.length;

    cnt.textContent = `${cart.item_count} Item${plural}`;
    const wasHidden =
      wrap.style.display === "none" ||
      wrap.getAttribute("aria-hidden") === "true";

    if (wasHidden) {
      wrap.classList.remove(
        "custom-sticky-cart--animate-in",
        "custom-sticky-cart--expanded",
        "custom-sticky-cart--compact",
        "custom-sticky-cart--reveal",
      );
      if (isFirstItem) {
        wrap.classList.add("custom-sticky-cart--compact");
        wrap.offsetHeight;
      } else {
        wrap.classList.add(
          "custom-sticky-cart--expanded",
          "custom-sticky-cart--reveal",
        );
      }
      wrap.style.removeProperty("display");
      wrap.setAttribute("aria-hidden", "false");
      wrap.offsetHeight;
      wrap.classList.add("custom-sticky-cart--animate-in");
      updatePosition();
      // Retry after a short delay to catch progress bar that appears with the cart add
      scheduleDelayedPositionUpdate(150);
      if (isFirstItem) {
        setTimeout(function () {
          wrap.classList.remove("custom-sticky-cart--compact");
          wrap.classList.add("custom-sticky-cart--expanded");
          setTimeout(function () {
            wrap.classList.add("custom-sticky-cart--reveal");
            updatePosition();
            // Final position check after expansion completes
            scheduleDelayedPositionUpdate(50);
          }, 350);
        }, 500);
      }
    } else if (isFirstItem) {
      wrap.classList.remove(
        "custom-sticky-cart--expanded",
        "custom-sticky-cart--reveal",
      );
      wrap.classList.add("custom-sticky-cart--compact");
      wrap.offsetHeight;
      setTimeout(function () {
        wrap.classList.remove("custom-sticky-cart--compact");
        wrap.classList.add("custom-sticky-cart--expanded");
        setTimeout(function () {
          wrap.classList.add("custom-sticky-cart--reveal");
          updatePosition();
          // Final position check after expansion completes
          scheduleDelayedPositionUpdate(50);
        }, 350);
      }, 500);
    } else {
      wrap.classList.remove("custom-sticky-cart--compact");
      wrap.classList.add(
        "custom-sticky-cart--expanded",
        "custom-sticky-cart--reveal",
      );
      updatePosition();
      // Retry after a short delay to catch progress bar changes
      scheduleDelayedPositionUpdate(150);
    }
  }

  function applyCart(cart, opts) {
    if (!cart) return;
    opts = opts || {};
    var prevCart = lastKnownCart;
    var lineRemoved = hasFullyRemovedLine(prevCart, cart);
    if (opts.detectFront !== false) {
      var bumped = detectFrontKeyFromCartDelta(prevCart, cart);
      if (bumped) rememberFrontKey(bumped);
    }
    var sig = cartSignature(cart) + "|f:" + (lastFrontKey || "");
    // Skip identical re-renders (optimistic → cart:change with same lines)
    if (sig === lastSignature && lastItemCount >= 0) {
      lastKnownCart = cart;
      return;
    }
    lastSignature = sig;
    lastKnownCart = cart;
    render(cart, { lineRemoved: lineRemoved });
  }

  function openShopifyCartDrawer() {
    const cartDrawer = document.querySelector("hdt-cart-drawer");
    if (cartDrawer && typeof cartDrawer.openDialog === "function") {
      // Use immediate mode (false) to skip animation and improve INP
      cartDrawer.openDialog(false);
      return;
    }
    if (cartDrawer) {
      // Use immediate attribute to skip animation
      cartDrawer.setAttribute("open", "immediate");
      return;
    }
    const cartIcon = document.querySelector('[aria-controls="CartDrawer"]');
    if (cartIcon) {
      cartIcon.click();
      return;
    }
    window.location.href = "/cart";
  }

  // Same quote pool as assets/me-page-transition.js.
  var CART_LOADER_QUOTES = [
    "Our Onion Hair Oil is loved by 10L+ customers across India.",
    "Each order you place contributes to planting a tree 🌳",
    "Mamaearth is Asia’s 1st brand with Made Safe Certified products.",
  ];

  function getCartLoaderQuote() {
    return CART_LOADER_QUOTES[Math.floor(Math.random() * CART_LOADER_QUOTES.length)];
  }

  // Picks a new quote on every call rather than caching one for the session,
  // so the message actually varies each time the overlay is shown.
  function renderCartLoaderQuote() {
    var el = document.getElementById("cart-immediate-loading-overlay-quote");
    if (!el) return;
    el.textContent = "“" + getCartLoaderQuote() + "”";
  }

  function showImmediateLoadingOverlay() {
    var overlay = document.getElementById("cart-immediate-loading-overlay");
    if (!overlay) return;
    // Open and close both fire this from pointerdown (instant INP feedback)
    // AND click (the actual action) for the same user gesture. Without this
    // guard the click's call re-appends an overlay that's already visible;
    // appendChild on a node that's already in the DOM still detaches and
    // reattaches it, which resets the CSS spin animation to its first frame —
    // this was the "loader restarts / mounts twice" bug.
    if (overlay.classList.contains("is-visible")) return;
    renderCartLoaderQuote();
    // Browsers clamp CSS z-index to the 32-bit signed max (2147483647), so
    // this overlay's z-index ties with the sticky pill's and the drawer's
    // (appendToBody) — both of which also exceed that ceiling and get
    // clamped to the same value. With tied z-index, stacking falls back to
    // DOM order, so re-append (move) the overlay to the end of <body> on
    // every show to guarantee it wins the tie, regardless of what else has
    // been appended to <body> since page load (e.g. the drawer reparenting
    // itself on open).
    document.body.appendChild(overlay);
    overlay.removeAttribute("hidden");
    overlay.classList.add("is-visible");
    overlay.setAttribute("aria-hidden", "false");
  }

  function hideImmediateLoadingOverlay() {
    var overlay = document.getElementById("cart-immediate-loading-overlay");
    if (!overlay) return;
    overlay.classList.remove("is-visible");
    overlay.setAttribute("aria-hidden", "true");
    overlay.setAttribute("hidden", "");
  }

  function afterNextPaint(fn) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        setTimeout(fn, 0);
      });
    });
  }

  // Shared by sticky cart open + cart drawer back/close paths
  window.HdtCartDrawerFeedback = {
    show: showImmediateLoadingOverlay,
    hide: hideImmediateLoadingOverlay,
    afterNextPaint: afterNextPaint,
  };

  var openInFlight = false;

  function startOpenCart() {
    if (openInFlight) return;
    openInFlight = true;

    // Show ASAP — prefer pointerdown so INP's next paint is the loader
    showImmediateLoadingOverlay();

    var safetyTimeout = setTimeout(function () {
      hideImmediateLoadingOverlay();
      openInFlight = false;
    }, 2000);

    var clearSafetyTimeout = function (event) {
      if (event.target && event.target.id === "CartDrawer") {
        clearTimeout(safetyTimeout);
        openInFlight = false;
        document.removeEventListener("dialog:afterOpen", clearSafetyTimeout);
      }
    };
    document.addEventListener("dialog:afterOpen", clearSafetyTimeout);

    if (typeof window.requestIdleCallback === "function") {
      requestIdleCallback(
        function () {
          if (typeof window.fireViewCart === "function") {
            window.fireViewCart("sticky_cart");
          }
        },
        { timeout: 100 }
      );
    } else {
      setTimeout(function () {
        if (typeof window.fireViewCart === "function") {
          window.fireViewCart("sticky_cart");
        }
      }, 0);
    }

    afterNextPaint(function () {
      openShopifyCartDrawer();
    });
  }

  // If the initial touch on the pill turns into a scroll/drag rather than a
  // tap, the browser cancels the synthetic click — startOpenCart() (and its
  // 2s safety timeout) never runs, so the overlay shown eagerly on pointerdown
  // would otherwise stay stuck on screen forever. Track movement between
  // pointerdown and pointerup/cancel so we can roll the overlay back once the
  // gesture is recognized as a scroll instead of a tap.
  var DRAG_THRESHOLD_PX = 10;
  var pointerStart = null;
  var pointerWasDrag = false;

  function endPointerTracking() {
    document.removeEventListener("pointermove", handlePointerMove);
    document.removeEventListener("pointerup", endPointerTracking);
    document.removeEventListener("pointercancel", endPointerTracking);
    pointerStart = null;
  }

  function handlePointerMove(e) {
    if (!pointerStart) return;
    var dx = e.clientX - pointerStart.x;
    var dy = e.clientY - pointerStart.y;
    if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) {
      pointerWasDrag = true;
      // Not a tap — roll back the eager loader so it doesn't linger while
      // the user keeps scrolling with the cart drawer never opening.
      if (!openInFlight) hideImmediateLoadingOverlay();
      endPointerTracking();
    }
  }


  function handlePointerDown(e) {
    if (e.button != null && e.button !== 0) return;
    pointerStart = { x: e.clientX, y: e.clientY };
    pointerWasDrag = false;
    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    document.addEventListener("pointerup", endPointerTracking, { passive: true });
    document.addEventListener("pointercancel", endPointerTracking, { passive: true });

    // Instant visual feedback at pointerdown (INP interaction start)
    showImmediateLoadingOverlay();
  }

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (pointerWasDrag) {
      // Gesture was recognized as a scroll/drag, not a tap — don't navigate.
      pointerWasDrag = false;
      hideImmediateLoadingOverlay();
      return;
    }
    startOpenCart();
  }

  function bind() {
    const btn = trigger();
    if (btn) {
      btn.removeEventListener("pointerdown", handlePointerDown);
      btn.removeEventListener("click", handleClick);
      btn.addEventListener("pointerdown", handlePointerDown, { passive: true });
      btn.addEventListener("click", handleClick);
    }
  }

  function onCartEvent(ev) {
    var cart = ev.detail && ev.detail.cart;
    if (cart && Array.isArray(cart.items)) {
      applyCart(cart);
      return;
    }
    // Rare: refresh without payload — fetch immediately (no 300ms lag)
    fetchCart().then(applyCart);
  }

  /**
   * Seed state from the server-rendered pill (sections/custom-sticky-cart.liquid)
   * so the pill is already visible/painted at first render instead of waiting on
   * DOMContentLoaded + a /cart.js round trip. The follow-up fetchCart() below
   * reconciles silently (same signature = no re-render) or updates in place if
   * the cart changed since the page was served (e.g. another tab).
   */
  function hydrateFromServerData() {
    const wrap = container();
    if (!wrap || wrap.getAttribute("data-hydrated") !== "1") return;
    const dataEl = document.getElementById("custom-sticky-cart-initial-data");
    if (!dataEl) return;
    var seedCart;
    try {
      seedCart = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!seedCart || !Array.isArray(seedCart.items) || !seedCart.items.length) {
      return;
    }

    lastKnownCart = seedCart;
    lastItemCount = seedCart.item_count || 0;
    lastLineCount = seedCart.items.length;

    var withImg = seedCart.items.filter(function (i) {
      return !!lineImage(i);
    });
    var newest = withImg[withImg.length - 1];
    if (newest) {
      lastFrontKey = lineKey(newest);
      for (var i = withImg.length - 2; i >= 0; i--) {
        prevFrontKey = lineKey(withImg[i]);
        break;
      }
    }
    lastSignature = cartSignature(seedCart) + "|f:" + (lastFrontKey || "");

    // Play the slide-up after a short, deliberate settle delay instead of
    // popping straight in — same animation as a live add, just not gated on
    // the /cart.js round trip anymore.
    if (wrap.classList.contains("custom-sticky-cart--pending-in")) {
      setTimeout(function () {
        wrap.classList.remove("custom-sticky-cart--pending-in");
        wrap.classList.add("custom-sticky-cart--animate-in");
        updatePosition();
      }, 120);
    }
  }

  function init() {
    bind();
    hydrateFromServerData();
    // Hide immediate loading overlay when drawer finishes opening or closing
    document.addEventListener("dialog:afterOpen", function (event) {
      if (event.target && event.target.id === "CartDrawer") {
        hideImmediateLoadingOverlay();
      }
    });

    document.addEventListener("dialog:afterClose", function (event) {
      if (event.target && event.target.id === "CartDrawer") {
        hideImmediateLoadingOverlay();
      }
    });
    
    // Initial hydrate; ignore stale empty cart.js if ATC already painted the pill
    fetchCart().then(function (cart) {
      if (
        lastKnownCart &&
        (lastKnownCart.item_count || 0) > 0 &&
        (!cart || !(cart.item_count || 0))
      ) {
        return;
      }
      applyCart(cart);
    });
    updatePosition();

    document.addEventListener("cart:change", onCartEvent);
    document.addEventListener("cart:refresh", onCartEvent);

    // Re-stack on scroll / chrome show-hide (same triggers as progress-bar offset)
    window.addEventListener("scroll", schedulePositionUpdate, { passive: true });
    window.addEventListener("resize", schedulePositionUpdate, { passive: true });
    window.addEventListener("orientationchange", schedulePositionUpdate, {
      passive: true,
    });
    window.addEventListener("load", schedulePositionUpdate, { once: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", schedulePositionUpdate, {
        passive: true,
      });
      window.visualViewport.addEventListener("scroll", schedulePositionUpdate, {
        passive: true,
      });
    }

    // Keep pill stacked above the free-delivery strip when that bar moves
    if (typeof MutationObserver !== "undefined") {
      const progress = document.querySelector(".cart-progressBar");
      if (progress) {
        // Watch for style, class, aria-hidden changes on the progress bar
        new MutationObserver(function () {
          schedulePositionUpdate();
          // Also schedule a delayed check in case the bar is mid-animation
          scheduleDelayedPositionUpdate(100);
        }).observe(progress, {
          attributes: true,
          attributeFilter: ["style", "class", "aria-hidden"],
        });

        // Watch for child changes (message content updates that affect height)
        // and the offers tab being shown/hidden, which adds height on top of
        // the bar without changing the bar's own box.
        new MutationObserver(schedulePositionUpdate).observe(progress, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ["hidden"],
        });
      }
      var wasMetaVisitor = document.body.classList.contains("meta-ad-visitor");
      new MutationObserver(function () {
        schedulePositionUpdate();
        var isMetaVisitor = document.body.classList.contains("meta-ad-visitor");
        if (isMetaVisitor !== wasMetaVisitor) {
          wasMetaVisitor = isMetaVisitor;
          if (lastKnownCart) render(lastKnownCart);
        }
      }).observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
      // Progress bar bottom is driven by --hdt-cart-progress-bottom on <html>
      new MutationObserver(schedulePositionUpdate).observe(
        document.documentElement,
        { attributes: true, attributeFilter: ["style"] },
      );

      // KwikPass login iframe (#iframe-kp.iframe-kp, z-index 1e8) sits under
      // the sticky pill (clamped to 2147483647). Toggle body.kwikpass-open so
      // CSS can hide the pill while the overlay is actually visible.
      var kpOverlayRaf = 0;
      var kpStyleObserver = null;
      var kpObservedEl = null;
      function getKwikpassOverlayEl() {
        return (
          document.getElementById("iframe-kp") ||
          document.querySelector("iframe.iframe-kp, .iframe-kp")
        );
      }
      function isKwikpassOverlayOpen() {
        var kp = getKwikpassOverlayEl();
        if (!kp) return false;
        var style = window.getComputedStyle(kp);
        if (style.display === "none" || style.visibility === "hidden") {
          return false;
        }
        if (parseFloat(style.opacity || "1") === 0) return false;
        return kp.getClientRects().length > 0;
      }
      function syncKwikpassOverlay() {
        document.body.classList.toggle(
          "kwikpass-open",
          isKwikpassOverlayOpen(),
        );
      }
      function watchKwikpassOverlayEl() {
        var kp = getKwikpassOverlayEl();
        if (!kp) {
          if (kpStyleObserver) {
            kpStyleObserver.disconnect();
            kpStyleObserver = null;
            kpObservedEl = null;
          }
          return;
        }
        if (kpObservedEl === kp) return;
        if (kpStyleObserver) {
          kpStyleObserver.disconnect();
          kpStyleObserver = null;
        }
        kpObservedEl = kp;
        kpStyleObserver = new MutationObserver(scheduleKwikpassOverlaySync);
        kpStyleObserver.observe(kp, {
          attributes: true,
          attributeFilter: ["style", "class", "hidden"],
        });
      }
      function scheduleKwikpassOverlaySync() {
        if (kpOverlayRaf) return;
        kpOverlayRaf = window.requestAnimationFrame(function () {
          kpOverlayRaf = 0;
          watchKwikpassOverlayEl();
          syncKwikpassOverlay();
        });
      }
      scheduleKwikpassOverlaySync();
      new MutationObserver(scheduleKwikpassOverlaySync).observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("metaAdApiFallback", function () {
      if (lastKnownCart) render(lastKnownCart);
      else fetchCart().then(applyCart);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
